// Per-phone UI language. Never written to Firebase / room state.
// Each device reads navigator.languages, matches a supported primary
// subtag (pt-BR → pt, zh-TW → zh, no → nb), and falls back to English.

import ar from './locales/ar.json';
import bg from './locales/bg.json';
import bn from './locales/bn.json';
import ca from './locales/ca.json';
import cs from './locales/cs.json';
import da from './locales/da.json';
import de from './locales/de.json';
import el from './locales/el.json';
import en from './locales/en.json';
import es from './locales/es.json';
import et from './locales/et.json';
import fa from './locales/fa.json';
import fi from './locales/fi.json';
import fr from './locales/fr.json';
import he from './locales/he.json';
import hi from './locales/hi.json';
import hr from './locales/hr.json';
import hu from './locales/hu.json';
import id from './locales/id.json';
import it from './locales/it.json';
import ja from './locales/ja.json';
import ko from './locales/ko.json';
import lt from './locales/lt.json';
import ms from './locales/ms.json';
import nb from './locales/nb.json';
import nl from './locales/nl.json';
import pl from './locales/pl.json';
import pt from './locales/pt.json';
import ro from './locales/ro.json';
import ru from './locales/ru.json';
import sk from './locales/sk.json';
import sl from './locales/sl.json';
import sr from './locales/sr.json';
import sv from './locales/sv.json';
import th from './locales/th.json';
import tr from './locales/tr.json';
import uk from './locales/uk.json';
import vi from './locales/vi.json';
import zh from './locales/zh.json';

export const dictionaries = {
  ar, bg, bn, ca, cs, da, de, el, en, es, et, fa, fi, fr, he, hi, hr, hu,
  id, it, ja, ko, lt, ms, nb, nl, pl, pt, ro, ru, sk, sl, sr, sv, th, tr,
  uk, vi, zh,
};

export const SUPPORTED_LOCALES = Object.keys(dictionaries).sort();
export const RTL_LOCALES = new Set(['he', 'ar', 'fa']);

const ALIASES = {
  no: 'nb',
  nn: 'nb',
  iw: 'he',
  in: 'id',
  jv: 'id',
  'zh-cn': 'zh',
  'zh-sg': 'zh',
  'zh-hans': 'zh',
  'zh-tw': 'zh',
  'zh-hk': 'zh',
  'zh-mo': 'zh',
  'zh-hant': 'zh',
};

const listeners = new Set();
let currentLocale = 'en';

function normalizeTag(raw) {
  return String(raw || '').trim().replace(/_/g, '-').toLowerCase();
}

export function resolveLocaleTag(raw) {
  const tag = normalizeTag(raw);
  if (!tag) return null;
  if (dictionaries[tag]) return tag;
  if (ALIASES[tag]) return ALIASES[tag];
  const primary = tag.split('-')[0];
  if (dictionaries[primary]) return primary;
  if (ALIASES[primary]) return ALIASES[primary];
  return null;
}

export function readBrowserLanguages() {
  if (typeof navigator === 'undefined') return [];
  const list = [];
  if (Array.isArray(navigator.languages)) list.push(...navigator.languages);
  if (navigator.language) list.push(navigator.language);
  if (navigator.userLanguage) list.push(navigator.userLanguage);
  return list;
}

export function readQueryLang(search) {
  if (typeof search === 'string') {
    try {
      return new URLSearchParams(search.startsWith('?') ? search : `?${search}`).get('lang');
    } catch {
      return null;
    }
  }
  if (typeof window === 'undefined') return null;
  try {
    return new URLSearchParams(window.location.search).get('lang');
  } catch {
    return null;
  }
}

export function detectLocale(languages, queryLang) {
  const hinted = resolveLocaleTag(queryLang === undefined ? readQueryLang() : queryLang);
  if (hinted) return hinted;
  const list = languages || readBrowserLanguages();
  for (const raw of list) {
    const match = resolveLocaleTag(raw);
    if (match) return match;
  }
  return 'en';
}

function lookup(dict, key) {
  if (!dict || !key) return undefined;
  if (Object.prototype.hasOwnProperty.call(dict, key)) return dict[key];
  return undefined;
}

export function interpolate(template, vars) {
  if (template == null) return '';
  if (!vars) return String(template);
  return String(template).replace(/\{(\w+)\}/g, (_, name) => (
    vars[name] == null ? `{${name}}` : String(vars[name])
  ));
}

function pluralKey(key, count) {
  return Number(count) === 1 ? `${key}_one` : `${key}_other`;
}

export function translate(locale, key, vars) {
  const loc = dictionaries[locale] ? locale : 'en';
  const dict = dictionaries[loc] || en;
  let template;
  if (vars && vars.count !== undefined) {
    template = lookup(dict, pluralKey(key, vars.count))
      ?? lookup(en, pluralKey(key, vars.count));
  }
  if (template == null) template = lookup(dict, key);
  if (template == null) template = lookup(en, key);
  if (template == null) return key;
  return interpolate(template, vars);
}

export function t(key, vars, locale) {
  return translate(locale || currentLocale, key, vars);
}

export function hasKey(key, locale = 'en') {
  const dict = dictionaries[locale];
  return !!(dict && lookup(dict, key) != null);
}

export function getLocale() {
  return currentLocale;
}

export function isRtlLocale(locale = currentLocale) {
  return RTL_LOCALES.has(locale);
}

export function applyDocumentLocale(locale = currentLocale) {
  if (typeof document === 'undefined') return;
  const root = document.documentElement;
  root.lang = locale;
  root.dir = isRtlLocale(locale) ? 'rtl' : 'ltr';
  root.dataset.locale = locale;
}

function notify() {
  listeners.forEach((fn) => {
    try { fn(currentLocale); } catch { /* ignore */ }
  });
}

export function subscribeLocale(fn) {
  listeners.add(fn);
  return () => listeners.delete(fn);
}

export function setLocale(locale) {
  const next = resolveLocaleTag(locale) || 'en';
  const changed = next !== currentLocale;
  currentLocale = next;
  applyDocumentLocale(next);
  if (changed) notify();
  return next;
}

export function bootLocale(options = {}) {
  const locale = detectLocale(options.languages, options.queryLang);
  setLocale(locale);
  if (typeof window !== 'undefined' && /(?:\?|&)test=1(?:&|$)/.test(window.location.search)) {
    window.__setLocale = setLocale;
  }
  return locale;
}

export function getDictionary(locale = currentLocale) {
  return dictionaries[locale] || en;
}

export function localeKeys(locale = 'en') {
  return Object.keys(dictionaries[locale] || en);
}

export function countKeysWithPrefix(prefix, locale = 'en') {
  const dict = dictionaries[locale] || en;
  let n = 0;
  while (lookup(dict, `${prefix}.${n}`) != null || lookup(en, `${prefix}.${n}`) != null) n += 1;
  return n;
}

export { en };
